import React from "react";
import { HelpCircle } from "lucide-react";
import { SimpleTooltip } from "./design-system";

// Tooltip definitions for per-game stats
const tooltipDefinitionsPerGame = {
  Rank: "The player's rank relative to others based on efficiency or performance.",
  Games: "Total number of games played by the player in the season.",
  MPG: "Average minutes played per game.",
  PPG: "Average points scored per game.",
  "FG%":
    "Field goal percentage: the percentage of all field goal attempts made.",
  "2P%": "Two-point field goal percentage: percentage of two-point shots made.",
  "3P%":
    "Three-point field goal percentage: percentage of three-point shots made.",
  "eFG%":
    "Effective field goal percentage accounts for the extra value of three-pointers.",
  "FT%": "Free throw percentage: percentage of free throws made.",
  RPG: "Rebounds per game (offensive + defensive).",
  APG: "Assists per game.",
  SPG: "Steals per game.",
  BPG: "Blocks per game.",
  TOPG: "Turnovers per game.",
  FPG: "Fouls committed per game.",
  EFF: "Efficiency rating: simplified measure of overall contribution.",
};

// 2. STATISTICS LEGEND ARRAY (Separate array for legend display)
const statisticsLegend = [
  { key: "Games", value: "Total games played" },
  { key: "MPG", value: "Minutes per game" },
  { key: "PPG", value: "Points per game" },
  { key: "FG%", value: "Field goal percentage" },
  { key: "2P%", value: "Two point field percentage" },
  { key: "3P%", value: "Three point field percentage" },
  { key: "eFG%", value: "Effective field goal percentage" },
  { key: "FT%", value: "Free throw percentage" },
  { key: "RPG", value: "Rebounds per game" },
  { key: "APG", value: "Assists per game" },
  { key: "SPG", value: "Steals per game" },
  { key: "BPG", value: "Blocks per game" },
  { key: "TOPG", value: "Turnovers per game" },
  { key: "FPG", value: "Fouls per game" },
  { key: "EFF", value: "Efficiency rating" },
];

const PerGameStatsTable = ({ stats }) => {
  const TableHeader = ({ children, hasTooltip = false }) => (
    <th
      className="py-3 px-4 text-left font-semibold text-[#999999]"
    >
      <div className="flex items-center gap-2">
        <span>{children}</span>
        {hasTooltip && tooltipDefinitionsPerGame[children] && (
          <SimpleTooltip title={tooltipDefinitionsPerGame[children]}>
            <HelpCircle
              size={12}
              style={{
                color: "#9ca3af",
                opacity: 0.7,
                cursor: "help",
              }}
            />
          </SimpleTooltip>
        )}
      </div>
    </th>
  );

  return (
    <div
      className="mt-4 bg-[#0A0A0A] rounded-[14px] shadow-sm border border-[#1A1A1A] overflow-hidden"
    >
      <div className="p-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead
              className="bg-[#111111] text-xs uppercase tracking-wider"
            >
              <tr>
                {/* <TableHeader hasTooltip>Rank</TableHeader> */}
                <TableHeader hasTooltip>Games</TableHeader>
                <TableHeader hasTooltip>MPG</TableHeader>
                <TableHeader hasTooltip>PPG</TableHeader>
                <TableHeader hasTooltip>FG%</TableHeader>
                <TableHeader hasTooltip>2P%</TableHeader>
                <TableHeader hasTooltip>3P%</TableHeader>
                <TableHeader hasTooltip>eFG%</TableHeader>
                <TableHeader hasTooltip>FT%</TableHeader>
                <TableHeader hasTooltip>RPG</TableHeader>
                <TableHeader hasTooltip>APG</TableHeader>
                <TableHeader hasTooltip>SPG</TableHeader>
                <TableHeader hasTooltip>BPG</TableHeader>
                <TableHeader hasTooltip>TOPG</TableHeader>
                <TableHeader hasTooltip>FPG</TableHeader>
                <TableHeader hasTooltip>EFF</TableHeader>
              </tr>
            </thead>
            <tbody
              className="bg-[#0A0A0A] divide-y divide-[#1A1A1A]"
            >
              <tr
                className="hover:bg-[rgba(255,255,255,0.04)] transition-colors"
              >
                {/* <td className="py-3 px-4 text-[#999999]">{stats?.rank || 'N/A'}</td> */}
                <td
                  className="py-3 px-4 text-[#999999]"
                >
                  {stats?.games || "N/A"}
                </td>
                <td
                  className="py-3 px-4 text-[#999999]"
                >
                  {stats?.minutes_per_game || "N/A"}
                </td>
                <td
                  className="py-3 px-4 text-[#999999] font-medium"
                >
                  {stats?.points_per_game || "N/A"}
                </td>
                <td
                  className="py-3 px-4 text-[#999999]"
                >
                  {stats?.field_goal_percentage || "N/A"}%
                </td>
                <td
                  className="py-3 px-4 text-[#999999]"
                >
                  {stats?.two_point_percentage || "N/A"}%
                </td>
                <td
                  className="py-3 px-4 text-[#999999]"
                >
                  {stats?.three_point_percentage || "N/A"}%
                </td>
                <td
                  className="py-3 px-4 text-[#999999]"
                >
                  {stats?.effective_field_goal_percentage || "N/A"}%
                </td>
                <td
                  className="py-3 px-4 text-[#999999]"
                >
                  {stats?.free_throw_percentage || "N/A"}%
                </td>
                <td
                  className="py-3 px-4 text-[#999999]"
                >
                  {stats?.rebounds_per_game || "N/A"}
                </td>
                <td
                  className="py-3 px-4 text-[#999999]"
                >
                  {stats?.assists_per_game || "N/A"}
                </td>
                <td
                  className="py-3 px-4 text-[#999999]"
                >
                  {stats?.steals_per_game || "N/A"}
                </td>
                <td
                  className="py-3 px-4 text-[#999999]"
                >
                  {stats?.blocks_per_game || "N/A"}
                </td>
                <td
                  className="py-3 px-4 text-[#999999]"
                >
                  {stats?.turnovers_per_game || "N/A"}
                </td>
                <td
                  className="py-3 px-4 text-[#999999]"
                >
                  {stats?.fouls_per_game || "N/A"}
                </td>
                <td
                  className="py-3 px-4 text-[#999999] font-medium"
                >
                  {stats?.efficiency || "N/A"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div
        className="mt-4 bg-[#0A0A0A] rounded-[14px] shadow-sm border border-[#1A1A1A] p-4"
      >
        <h3
          className="text-lg font-semibold mb-3 text-[#F5F5F5]"
        >
          Statistics Legend
        </h3>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
          {statisticsLegend.map((item, index) => (
            <div
              key={index}
              className="p-2 rounded bg-[#111111]"
            >
              <div
                className="font-medium text-sm text-[#F5F5F5]"
              >
                {item.key}
              </div>
              <div
                className="text-xs mt-1 text-[#555555]"
              >
                {item.value}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PerGameStatsTable;
